import { useCallback, useState } from 'react';
import { useMantineColorScheme } from '@mantine/core';
import { charlieSchemeAttribute } from './theme';

/**
 * @desc The three selectable app schemes. Mantine itself only knows light/dark;
 * `blue` is the Charlie all-navy chrome (Figma "ChatBot Screens Blue") layered on
 * top of the dark scheme via a document-root attribute — see
 * `apps/portal/src/styles/blue-scheme.css.ts`.
 */
export const charlieSchemes = ['light', 'dark', 'blue'] as const;

export type CharlieScheme = (typeof charlieSchemes)[number];

/**
 * Kept separate from Mantine's own key (`mantine-color-scheme-value`), which
 * still holds the underlying light/dark scheme and stays authoritative for it.
 * The pre-paint script in `index.html` reads both keys before the bundle loads.
 */
export const charlieSchemeStorageKey = 'charlie-scheme';

function isCharlieScheme(value: unknown): value is CharlieScheme {
  return charlieSchemes.includes(value as CharlieScheme);
}

function readStoredScheme(): CharlieScheme | undefined {
  try {
    const stored = window.localStorage.getItem(charlieSchemeStorageKey);
    return isCharlieScheme(stored) ? stored : undefined;
  } catch {
    // Storage can be unavailable (private mode, blocked cookies); fall back to
    // whatever Mantine resolves on its own rather than failing to render.
    return undefined;
  }
}

function applySchemeAttribute(scheme: CharlieScheme): void {
  if (scheme === 'blue') {
    document.documentElement.setAttribute(charlieSchemeAttribute, 'blue');
  } else {
    document.documentElement.removeAttribute(charlieSchemeAttribute);
  }
}

/**
 * @desc Three-way scheme state. `blue` maps onto Mantine's dark scheme plus the
 * root attribute; light and dark clear the attribute and behave as before.
 */
export function useCharlieScheme(): {
  scheme: CharlieScheme;
  setScheme: (scheme: CharlieScheme) => void;
} {
  const { colorScheme, setColorScheme } = useMantineColorScheme();
  const [stored, setStored] = useState<CharlieScheme | undefined>(readStoredScheme);

  // Mantine's scheme wins where the two disagree — e.g. the user cleared our key,
  // or another surface set light/dark directly.
  const mantineScheme: CharlieScheme = colorScheme === 'light' ? 'light' : 'dark';
  const scheme = stored === 'blue' && mantineScheme === 'dark' ? 'blue' : mantineScheme;

  const setScheme = useCallback(
    (next: CharlieScheme) => {
      setStored(next);
      applySchemeAttribute(next);
      setColorScheme(next === 'blue' ? 'dark' : next);
      try {
        window.localStorage.setItem(charlieSchemeStorageKey, next);
      } catch {
        // Non-fatal: the scheme still applies for this session.
      }
    },
    [setColorScheme],
  );

  return { scheme, setScheme };
}
